import React, {useState, useEffect} from 'react';
import {Modal, Button, Form, InputGroup, Alert, Spinner} from 'react-bootstrap';
import {EmailAccount} from '../DomainEmailAccounts';

interface EditEmailAccountModalProps {
  show: boolean;
  onHide: () => void;
  account: EmailAccount | null;
  onSave: (account: EmailAccount, password?: string) => Promise<void>;
}

const EditEmailAccountModal = ({
  show,
  onHide,
  account,
  onSave,
}: EditEmailAccountModalProps): JSX.Element => {
  const [formData, setFormData] = useState({
    name: '',
    quota: '',
    motDePasse: '',
    confirmMotDePasse: '',
    active: true,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [validated, setValidated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    if (account && show) {
      setFormData({
        name: account.name,
        quota: String(account.quota / 1000000 / 1000000), // Conversion octets vers Mo
        motDePasse: '',
        confirmMotDePasse: '',
        active: account.active,
      });
      setPasswordError('');
      setShowPassword(false);
      setShowConfirmPassword(false);
      setValidated(false);
      setIsLoading(false);
      setApiError('');
    }
  }, [account, show]);

  const handleChange = (field: string, value: string | boolean) => {
    setFormData({
      ...formData,
      [field]: value,
    });

    if (field === 'motDePasse' || field === 'confirmMotDePasse') {
      setPasswordError('');
    }

    if (apiError) {
      setApiError('');
    }
  };

  const validatePasswords = (): boolean => {
    // Si aucun mot de passe n'est saisi, c'est valide (optionnel)
    if (!formData.motDePasse && !formData.confirmMotDePasse) {
      return true;
    }

    // Si un seul champ est rempli
    if (formData.motDePasse && !formData.confirmMotDePasse) {
      setPasswordError('Veuillez confirmer le mot de passe');
      return false;
    }

    if (!formData.motDePasse && formData.confirmMotDePasse) {
      setPasswordError('Veuillez saisir le nouveau mot de passe');
      return false;
    }

    // Si les deux champs sont remplis, vérifier la longueur et la correspondance
    if (formData.motDePasse.length < 12) {
      setPasswordError('Le mot de passe doit contenir au moins 12 caractères');
      return false;
    }

    if (formData.motDePasse !== formData.confirmMotDePasse) {
      setPasswordError('Les mots de passe ne correspondent pas');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const form = e.currentTarget as HTMLFormElement;
    setValidated(true);

    if (!form.checkValidity()) {
      e.stopPropagation();
      return;
    }

    if (!validatePasswords()) {
      return;
    }

    if (account) {
      setIsLoading(true);
      setApiError('');

      try {
        const updatedAccount: EmailAccount = {
          ...account,
          name: formData.name,
          quota: Number(formData.quota) * 1000000, // Conversion Mo vers octets
          active: formData.active,
        };

        // Passer le mot de passe seulement s'il a été saisi
        const password = formData.motDePasse.trim() ? formData.motDePasse : undefined;
        await onSave(updatedAccount, password);

        onHide();
      } catch (error: any) {
        console.error('Erreur lors de la modification:', error);

        if (error.response?.data?.message) {
          setApiError(error.response.data.message);
        } else if (error.response?.status === 404) {
          setApiError('Le compte n\'existe pas');
        } else if (error.response?.status === 409) {
          setApiError('Un compte avec cet identifiant existe déjà');
        } else if (error.response?.status === 400) {
          setApiError('Données invalides. Veuillez vérifier les informations saisies');
        } else {
          setApiError('Erreur lors de la modification du compte. Veuillez réessayer');
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onHide();
    }
  };

  if (!account) return <></>;

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa-solid fa-pen me-2 text-primary"></i>
          Modifier le compte de messagerie
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {apiError && (
          <Alert variant="danger" dismissible onClose={() => setApiError('')}>
            <i className="fa-solid fa-circle-exclamation me-2"></i>
            {apiError}
          </Alert>
        )}

        <Alert variant="secondary" className="mb-4">
          <div className="d-flex align-items-center">
            <i className="fa-solid fa-envelope fs-3 text-primary me-3"></i>
            <div>
              <strong className="d-block text-dark">Compte : {account.username}</strong>
              <small className="text-muted">Modification des paramètres du compte</small>
            </div>
          </div>
        </Alert>

        <Form noValidate validated={validated} id="editEmailForm" onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label className="required">Nom</Form.Label>
            <Form.Control
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Ex: Contact, Support, Admin..."
              required
              isInvalid={validated && !formData.name}
              disabled={isLoading}
            />
            <Form.Control.Feedback type="invalid">
              Veuillez saisir un nom pour le compte.
            </Form.Control.Feedback>
            <Form.Text className="text-muted">Nom d'affichage du compte</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="required">Quota</Form.Label>
            <InputGroup hasValidation>
              <Form.Control
                type="number"
                value={formData.quota}
                onChange={(e) => handleChange('quota', e.target.value)}
                placeholder="500"
                required
                min="1"
                step="1"
                isInvalid={validated && (!formData.quota || Number(formData.quota) < 1)}
                disabled={isLoading}
              />
              <InputGroup.Text className="bg-light">
                <strong>Mo</strong>
              </InputGroup.Text>
              <Form.Control.Feedback type="invalid">
                Veuillez saisir un quota valide (minimum 1 Mo).
              </Form.Control.Feedback>
            </InputGroup>
            <Form.Text className="text-muted">
              Espace de stockage alloué au compte. Occupation actuelle :{' '}
              <strong>
                {account.quotaUsage ? `${(account.quotaUsage / 1000000).toFixed(0)} Mo` : '0 Mo'}
              </strong>
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>État du compte</Form.Label>
            <div className="d-flex align-items-center gap-3 p-3 bg-light rounded">
              <Form.Check
                type="switch"
                id="account-status-switch"
                checked={formData.active}
                onChange={(e) => handleChange('active', e.target.checked)}
                label=""
                disabled={isLoading}
              />
              <div>
                <strong>{formData.active ? 'Actif' : 'Inactif'}</strong>
                <p className="mb-0 text-muted small">
                  {formData.active
                    ? 'Le compte peut envoyer et recevoir des emails'
                    : 'Le compte est désactivé et ne peut pas recevoir d\'emails'}
                </p>
              </div>
            </div>
          </Form.Group>

          <hr className="my-4" />

          <h6 className="mb-3">
            <i className="fa-solid fa-key me-2 text-warning"></i>
            Modifier le mot de passe (optionnel)
          </h6>

          <Form.Group className="mb-3">
            <Form.Label>Nouveau mot de passe</Form.Label>
            <InputGroup hasValidation>
              <Form.Control
                type={showPassword ? 'text' : 'password'}
                value={formData.motDePasse}
                onChange={(e) => handleChange('motDePasse', e.target.value)}
                placeholder="Laisser vide pour ne pas modifier"
                minLength={12}
                isInvalid={!!passwordError && (passwordError.includes('12 caractères') || passwordError.includes('saisir le nouveau'))}
                disabled={isLoading}
              />
              <Button
                variant="outline-secondary"
                onClick={() => setShowPassword(!showPassword)}
                title={showPassword ? 'Masquer' : 'Afficher'}
                type="button"
                disabled={isLoading}
              >
                <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </Button>
              <Form.Control.Feedback type="invalid">
                {passwordError}
              </Form.Control.Feedback>
            </InputGroup>
            <Form.Text className="text-muted">
              Le mot de passe doit contenir au moins 12 caractères
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Confirmer le nouveau mot de passe</Form.Label>
            <InputGroup hasValidation>
              <Form.Control
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmMotDePasse}
                onChange={(e) => handleChange('confirmMotDePasse', e.target.value)}
                placeholder="Confirmer le mot de passe"
                minLength={12}
                isInvalid={!!passwordError && (passwordError.includes('correspondent pas') || passwordError.includes('confirmer'))}
                disabled={isLoading}
              />
              <Button
                variant="outline-secondary"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                title={showConfirmPassword ? 'Masquer' : 'Afficher'}
                type="button"
                disabled={isLoading}
              >
                <i className={`fa-solid ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </Button>
              <Form.Control.Feedback type="invalid">
                {passwordError}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>

          <div className="alert alert-light-info d-flex align-items-center">
            <i className="fa-solid fa-circle-info fs-3 me-3"></i>
            <div>
              <strong>Information :</strong> Si vous modifiez le mot de passe, l'utilisateur devra
              utiliser le nouveau mot de passe pour accéder à sa messagerie. Laisser les champs vides
              pour ne pas modifier le mot de passe.
            </div>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
          <i className="fa-solid fa-xmark me-2"></i>
          Annuler
        </Button>
        <Button variant="primary" type="submit" form="editEmailForm" disabled={isLoading}>
          {isLoading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Enregistrement en cours...
            </>
          ) : (
            <>
              <i className="fa-solid fa-save me-2"></i>
              Enregistrer les modifications
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditEmailAccountModal;
